import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { appHref, go } from "../lib/hosts";
import HeroScoreViz from "../components/landing/HeroScoreViz";
import {
  ArrowRight, Building2, ChevronDown, Compass, FileText, Layers, MapPin, Radar, Store,
  Database, ShieldCheck, Sparkles, MessageCircle, Mail,
} from "lucide-react";

// The product's own front door at landhep.id. It borrows verified facts from the
// marketing showcase (nextops-website/public/showcase/landhep) but speaks as the
// product, not about it: the calls to action are "Daftar" and "Masuk", the three
// product lines are all here (the showcase tells one story), and every picture is a
// real screen from the app — no illustrative mockups.
//
// Every number below was read from the production database and the code on
// 2026-09-28. Update them here when the data changes; do not round up.

const STATS = [
  { n: "83.452", label: "kelurahan dinilai" },
  { n: "34", label: "provinsi" },
  { n: "24,9 juta", label: "sel grid 100 m" },
  { n: "219", label: "subkategori bisnis" },
  { n: "1–10 km", label: "radius analisis" },
];

const DOORS = [
  {
    icon: Compass,
    kicker: "Cari lokasi",
    title: "Mau buka atau ekspansi outlet?",
    who: "Pemilik usaha dan tim ekspansi",
    body: "Setiap sel 100 m di kota Anda punya skor untuk jenis bisnis Anda. Bandingkan hingga lima titik, lalu buka Deep Analysis untuk kandidat terkuat.",
    points: ["Peta skor per sel 100 m", "Perbandingan hingga 5 titik", "Deep Analysis radius 1–10 km, bisa diunduh PDF"],
  },
  {
    icon: Building2,
    kicker: "Punya ruko atau aset?",
    title: "Bisnis apa yang paling cocok di titik ini?",
    who: "Pemilik properti dan pengelola aset",
    body: "Potensi Bisnis mengurutkan usaha yang paling berpeluang di lokasi Anda. Potensi Iklan menyusun proposal untuk calon pengiklan — dengan angka, bukan janji.",
    points: ["Peringkat bisnis berpotensi", "Proposal siap kirim ke pengiklan", "Laporan per aset, bisa diunduh PDF"],
  },
  {
    icon: Radar,
    kicker: "Jaringan cabang",
    title: "Puluhan toko, satu radar.",
    who: "Brand dengan banyak outlet",
    body: "Branch Radar memantau kompetitor di sekitar setiap cabang Anda dan menandai perubahan permintaan — sehingga tim tahu cabang mana yang perlu perhatian.",
    points: ["Unggah daftar toko dari CSV", "Kompetitor di sekitar tiap cabang", "Pemindaian berkala"],
    contact: true,
  },
];

const STEPS = [
  {
    n: "1",
    title: "Ceritakan bisnis Anda",
    body: "Jenis usaha, target pelanggan, kelas harga, dan hal yang paling penting bagi Anda — misalnya visibilitas jalan atau dekat kampus. Cukup sekali.",
  },
  {
    n: "2",
    title: "Baca peta skor",
    body: "Setiap sel di kota target langsung berwarna sesuai kecocokannya dengan profil Anda. Klik sel mana pun untuk melihat alasannya.",
  },
  {
    n: "3",
    title: "Bandingkan kandidat",
    body: "Masukkan hingga lima titik ke perbandingan. Skor, demografi, dan kompetitor ditampilkan berdampingan.",
  },
  {
    n: "4",
    title: "Buka Deep Analysis",
    body: "Untuk kandidat terkuat, jalankan analisis radius 1–10 km: kompetitor nyata dari Google Maps, ekosistem sekitar, harga properti, dan rencana aksi. Unduh sebagai PDF.",
  },
];

const REPORT_SECTIONS = [
  ["Profil kelurahan", "karakter area, pola trafik, transportasi umum"],
  ["Populasi & demografi", "jumlah, gender, struktur usia dalam radius"],
  ["Ekosistem pendukung", "kampus, kantor, hotel, minimarket, transit — di peta"],
  ["Kepadatan POI", "seberapa padat tempat usaha di sekitar titik"],
  ["Kompetitor terdekat", "dari Google Maps, dengan peta khusus dan tautan ke tiap tempat"],
  ["Pembacaan pasar", "kejenuhan, kualitas pesaing, keberlangsungan, posisi harga"],
  ["Suara pelanggan", "apa yang dipuji dan dikeluhkan di ulasan pesaing"],
  ["Properti & sewa", "kisaran harga tanah dan sewa di area"],
  ["Ritme area", "kapan area ini ramai, dicocokkan dengan jam operasional"],
  ["Rencana aksi", "langkah konkret yang disarankan untuk titik ini"],
];

const DATA_SOURCES = [
  { icon: Database, title: "Sensus & populasi", body: "Jumlah penduduk, gender, dan struktur usia per sel grid 100 m — dasar setiap hitungan jangkauan." },
  { icon: MapPin, title: "OpenStreetMap", body: "569.896 titik minat dan 271 ribu poligon guna lahan di seluruh Indonesia, untuk ekosistem dan karakter area." },
  { icon: Store, title: "Google Maps", body: "Kompetitor, rating, dan ulasan nyata di sekitar titik yang Anda pilih — diambil langsung saat laporan dibuat." },
  { icon: Layers, title: "Harga properti", body: "Kisaran harga tanah dan sewa per kelurahan, sebagai pembanding anggaran dan penanda daya beli." },
];

const FAQS = [
  {
    q: "Apa yang saya dapat setelah mendaftar?",
    a: "Akun Anda dibuat seketika. Setelah itu tim kami menghubungi Anda untuk aktivasi: membuka kota yang Anda butuhkan dan mengisi kredit. Tanpa aktivasi, akun belum bisa menjalankan analisis.",
  },
  {
    q: "Bagaimana cara bayarnya?",
    a: "Prabayar dengan kredit. Setiap laporan Deep Analysis, Potensi Bisnis, atau Potensi Iklan memotong kredit sesuai tarifnya, dan setiap kota dibuka untuk satu tahun. Tidak ada langganan bulanan. Besarannya bergantung pada area yang ingin Anda buka — bicarakan langsung dengan tim kami.",
  },
  {
    q: "Seberapa akurat skornya?",
    a: "Semua angka dihitung dari data — sensus, OpenStreetMap, Google Maps, harga properti — sebelum AI dilibatkan. AI hanya menjelaskan angka itu, tidak boleh mengubah peringkat atau mengarang angka. Input yang sama selalu menghasilkan angka yang sama, dan setiap laporan menyebut sumbernya.",
  },
  {
    q: "Apa bedanya dengan survei lapangan?",
    a: "Survei lapangan tetap perlu, tapi untuk lokasi yang sudah masuk shortlist. Landhep membantu Anda menyusun shortlist itu: membaca ratusan sel sekaligus, menyaring yang tidak cocok, dan membawa Anda ke lapangan dengan pertanyaan yang tepat.",
  },
  {
    q: "Bisnis saya spesifik. Apakah tetap relevan?",
    a: "Ada 17 kategori dan 219 subkategori bisnis, dan profil Anda — target pelanggan, kelas harga, prioritas lokasi — mengubah skornya. Dua bisnis berbeda melihat peta yang berbeda untuk kota yang sama.",
  },
  {
    q: "Kota mana saja yang tersedia?",
    a: "Data grid mencakup 34 provinsi. Anda memilih kota target saat onboarding, dan kota lain bisa dibuka kapan saja lewat tim kami.",
  },
  {
    q: "Apakah data bisnis saya aman?",
    a: "Profil bisnis dan lokasi yang Anda analisis hanya dipakai untuk mempersonalisasi analisis Anda sendiri, dan tidak dibagikan ke pengguna lain.",
  },
];

const NAV_LINKS = [
  { label: "Produk", id: "produk" },
  { label: "Cara kerja", id: "cara-kerja" },
  { label: "Laporan", id: "laporan" },
  { label: "Data", id: "data" },
  { label: "FAQ", id: "faq" },
];

const WA_URL = "https://wa.me/6285111214177";
const MAIL = "info@landhep.id";

// A screenshot inside a slim browser frame, so a real screen reads as a product
// picture rather than a stray image.
function Screen({ src, alt, className = "" }) {
  return (
    <figure className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl ${className}`}>
      <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-2">
        <span className="h-2 w-2 rounded-full bg-slate-300" />
        <span className="h-2 w-2 rounded-full bg-slate-300" />
        <span className="h-2 w-2 rounded-full bg-slate-300" />
      </div>
      <img src={src} alt={alt} loading="lazy" className="block w-full" />
    </figure>
  );
}

function Eyebrow({ children, light = false }) {
  return (
    <span className={`text-xs font-semibold uppercase tracking-[0.2em] ${light ? "text-brand-300" : "text-brand-700"}`}>
      {children}
    </span>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id === "top" ? null : e.target.id); }),
      { rootMargin: "-40% 0px -55% 0px" },
    );
    // The hero is observed too, so scrolling back up clears the highlight.
    ["top", ...NAV_LINKS.map((l) => l.id)].forEach((id) => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  // The app shell locks the document to the viewport; the landing page scrolls.
  useEffect(() => {
    const els = [document.documentElement, document.body, document.getElementById("root")];
    els.forEach((el) => { if (el) { el.style.overflow = "auto"; el.style.height = "auto"; } });
    return () => { els.forEach((el) => { if (el) { el.style.overflow = ""; el.style.height = ""; } }); };
  }, []);

  // The app is on platform.landhep.id; on the landing host this crosses over.
  const goAuth = () => go(navigate, appHref("/auth"));

  return (
    <div className="min-h-screen bg-brand-50 text-[#171C1F] antialiased">
      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav className="fixed top-0 z-50 w-full border-b border-slate-100 bg-white/90 shadow-sm backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between px-6 py-3.5 md:px-10">
          <a href="#top" className="flex items-center gap-2 text-xl font-extrabold tracking-tight text-brand-700"><img src="/landhep-icon.png" alt="" className="h-7 w-7" />Landhep</a>
          <div className="hidden items-center gap-7 md:flex">
            {NAV_LINKS.map(({ label, id }) => (
              <a key={id} href={`#${id}`}
                className={activeSection === id
                  ? "border-b-2 border-brand-700 pb-0.5 text-sm font-semibold text-brand-700"
                  : "text-sm font-medium text-slate-600 transition-colors hover:text-brand-600"}>
                {label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={goAuth} className="rounded-full px-4 py-2 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50">
              Masuk
            </button>
            <button onClick={goAuth} className="rounded-full bg-brand-700 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-800">
              Daftar
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section id="top" className="relative overflow-hidden bg-brand-900 pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(153,206,183,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(153,206,183,0.08)_1px,transparent_1px)] bg-[size:48px_48px]" />
        <div className="relative mx-auto grid w-full max-w-[1200px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[1.05fr_1fr]">
          <div className="space-y-7">
            <Eyebrow light>Location intelligence untuk Indonesia</Eyebrow>
            <h1 className="text-4xl font-extrabold leading-[1.1] text-white md:text-5xl" style={{ textWrap: "balance" }}>
              Setiap titik di Indonesia sudah kami nilai. Tinggal pilih yang cocok untuk bisnis Anda.
            </h1>
            <p className="max-w-xl text-lg leading-relaxed text-brand-100/80">
              Landhep menilai setiap sel 100 m di 34 provinsi dari demografi, keramaian, kompetitor, dan harga
              properti, lalu mencocokkannya dengan profil bisnis Anda. Keputusan lokasi jadi berdasar data, bukan kata orang.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={goAuth}
                className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-7 py-3.5 font-semibold text-white shadow-lg shadow-brand-900/30 transition-colors hover:bg-brand-400">
                Daftar <ArrowRight className="h-4 w-4" />
              </button>
              <a href="#cara-kerja" className="rounded-full border border-white/20 px-7 py-3.5 font-semibold text-white transition-colors hover:bg-white/10">
                Lihat cara kerja
              </a>
            </div>
            <p className="text-sm text-brand-100/60">Setelah mendaftar, tim kami membantu aktivasi akun Anda.</p>
          </div>
          <HeroScoreViz />
        </div>
        <div className="relative mx-auto mt-14 grid w-full max-w-[1200px] grid-cols-2 gap-6 border-t border-white/10 px-6 pt-8 md:grid-cols-5 md:px-10">
          {STATS.map(({ n, label }) => (
            <div key={label}>
              <p className="text-2xl font-extrabold tabular-nums text-white md:text-3xl">{n}</p>
              <p className="mt-1 text-sm text-brand-100/60">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Three doors ─────────────────────────────────────────────────── */}
      <section id="produk" className="py-20 md:py-24">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10">
          <div className="mb-12 max-w-2xl space-y-3">
            <Eyebrow>Produk</Eyebrow>
            <h2 className="text-3xl font-bold md:text-4xl" style={{ textWrap: "balance" }}>Tiga pertanyaan lokasi, satu platform</h2>
            <p className="leading-relaxed text-[#3E4947]">
              Mencari lokasi, menawarkan aset, atau menjaga jaringan cabang — data di baliknya sama, laporannya disesuaikan dengan siapa yang bertanya.
            </p>
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            {DOORS.map(({ icon: Icon, kicker, title, who, body, points, contact }) => (
              <article key={kicker} className="flex flex-col rounded-2xl border border-brand-200/70 bg-white p-7 shadow-sm">
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                  <Icon className="h-5 w-5" />
                </div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-700">{kicker}</p>
                <h3 className="mt-2 text-xl font-bold">{title}</h3>
                <p className="mt-1 text-sm text-slate-500">Untuk {who.toLowerCase()}</p>
                <p className="mt-4 leading-relaxed text-[#3E4947]">{body}</p>
                <ul className="mt-5 space-y-2 text-sm text-[#3E4947]">
                  {points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />{pt}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-6">
                  {contact ? (
                    <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-700 hover:underline">
                      Hubungi tim <ArrowRight className="h-4 w-4" />
                    </a>
                  ) : (
                    <button onClick={goAuth} className="inline-flex items-center gap-2 text-sm font-semibold text-brand-700 hover:underline">
                      Mulai <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────── */}
      <section id="cara-kerja" className="bg-white py-20 md:py-24">
        <div className="mx-auto grid w-full max-w-[1200px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <Eyebrow>Cara kerja</Eyebrow>
            <h2 className="mt-3 text-3xl font-bold md:text-4xl" style={{ textWrap: "balance" }}>Dari profil bisnis ke keputusan, dalam empat langkah</h2>
            <ol className="mt-10 space-y-8">
              {STEPS.map(({ n, title, body }) => (
                <li key={n} className="flex gap-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-700 text-base font-extrabold text-white">{n}</span>
                  <div>
                    <h3 className="text-lg font-bold">{title}</h3>
                    <p className="mt-1 leading-relaxed text-[#3E4947]">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="space-y-6">
            <Screen src="/landing/skenario.jpg" alt="Peta potensi di Skenario: layer potensi, populasi, dan keramaian POI untuk Kota Surakarta" />
            <Screen src="/landing/peta-lokasi.jpg" alt="Peta lokasi Deep Analysis: titik Anda, kompetitor, dan belasan kategori ekosistem di sekitarnya" />
          </div>
        </div>
      </section>

      {/* ── Inside a report ─────────────────────────────────────────────── */}
      <section id="laporan" className="py-20 md:py-24">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10">
          <div className="mb-12 max-w-2xl space-y-3">
            <Eyebrow>Isi laporan</Eyebrow>
            <h2 className="text-3xl font-bold md:text-4xl" style={{ textWrap: "balance" }}>Bukan skor saja — alasannya ikut</h2>
            <p className="leading-relaxed text-[#3E4947]">
              Deep Analysis membaca radius 1–10 km di sekitar titik yang Anda pilih. Setiap bagian punya sumber yang disebut, dan seluruhnya bisa diunduh sebagai PDF untuk dibawa ke rapat.
            </p>
          </div>
          <div className="grid gap-8 lg:grid-cols-[1.15fr_1fr]">
            <div className="space-y-6">
              <Screen src="/landing/peta-kompetitor.jpg" alt="Analisis kompetitor terdekat: intensitas kompetisi, peta kompetitor bernomor, dan tabel ancaman per pesaing" />
              <Screen src="/landing/demografi.jpg" alt="Populasi dalam radius: total, gender, struktur usia, dan kelompok usia produktif dari data sensus" />
            </div>
            <div className="rounded-2xl border border-brand-200/70 bg-white p-7 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-700">Sepuluh bagian dalam satu laporan</p>
              <dl className="mt-5 divide-y divide-slate-100">
                {REPORT_SECTIONS.map(([t, d]) => (
                  <div key={t} className="grid gap-1 py-3 sm:grid-cols-[170px_1fr] sm:gap-4">
                    <dt className="font-semibold">{t}</dt>
                    <dd className="text-sm text-[#3E4947]">{d}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-6 flex items-center gap-2 rounded-xl bg-brand-50 px-4 py-3 text-sm text-brand-800">
                <FileText className="h-4 w-4 shrink-0" /> Setiap nama tempat di laporan membuka lokasinya di Google Maps.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Data & method ───────────────────────────────────────────────── */}
      <section id="data" className="bg-brand-900 py-20 text-white md:py-24">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr]">
            <div>
              <Eyebrow light>Data & cara menghitung</Eyebrow>
              <h2 className="mt-3 text-3xl font-bold md:text-4xl" style={{ textWrap: "balance" }}>Angkanya dihitung dulu. AI hanya menjelaskan.</h2>
              <p className="mt-5 leading-relaxed text-brand-100/80">
                Skor, jarak, jumlah pesaing, dan jangkauan populasi dihitung deterministik dari data — sebelum model AI dilibatkan.
                AI menerima angka itu dan menuliskan artinya; ia tidak boleh mengubah peringkat atau mengarang angka.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-brand-100/80">
                <li className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-300" /> Input yang sama selalu menghasilkan angka yang sama — laporan bisa diaudit ulang.</li>
                <li className="flex items-start gap-3"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand-300" /> Kalau AI gagal, yang hilang hanya narasinya. Datanya tetap ada.</li>
              </ul>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {DATA_SOURCES.map(({ icon: Icon, title, body }) => (
                <div key={title} className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <Icon className="h-5 w-5 text-brand-300" />
                  <h3 className="mt-4 font-bold">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-100/70">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing model ───────────────────────────────────────────────── */}
      <section className="py-20 md:py-24">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10">
          <div className="max-w-2xl">
            <Eyebrow>Cara bayar</Eyebrow>
            <h2 className="mt-3 text-3xl font-bold md:text-4xl" style={{ textWrap: "balance" }}>Bayar untuk laporan yang Anda pakai</h2>
            <p className="mt-5 leading-relaxed text-[#3E4947]">
              Tidak ada langganan bulanan. Anda mengisi kredit, membuka kota yang dibutuhkan untuk satu tahun, lalu setiap laporan memotong kredit sesuai tarifnya. Besarannya bergantung pada area yang ingin Anda buka — jadi kami bicarakan langsung.
            </p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {[
              ["Kredit prabayar", "Diisi lewat tim kami, dipakai untuk laporan apa pun."],
              ["Kota per tahun", "Sekali buka, seluruh sel di kota itu bisa dijelajahi setahun."],
              ["Per laporan", "Deep Analysis, Potensi Bisnis, dan Potensi Iklan punya tarif masing-masing, dikonfirmasi sebelum dipotong."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-2xl border border-brand-200/70 bg-white p-6 shadow-sm">
                <h3 className="font-bold">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#3E4947]">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section id="faq" className="bg-white py-20 md:py-24">
        <div className="mx-auto w-full max-w-[820px] px-6">
          <div className="mb-10 text-center">
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-3 text-3xl font-bold md:text-4xl">Pertanyaan yang sering muncul</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map(({ q, a }, i) => {
              const open = openFaq === i;
              return (
                <article key={q} className="overflow-hidden rounded-xl border border-slate-200">
                  <button onClick={() => setOpenFaq(open ? -1 : i)} aria-expanded={open}
                    className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-brand-50">
                    <span className="font-semibold">{q}</span>
                    <ChevronDown className={`h-5 w-5 shrink-0 text-brand-700 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && <div className="border-t border-slate-100 px-6 pb-5 pt-4 leading-relaxed text-[#3E4947]">{a}</div>}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────── */}
      <section className="py-20 md:py-24">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10">
          <div className="rounded-3xl bg-brand-700 px-8 py-12 text-white md:px-14 md:py-16">
            <div className="grid items-center gap-8 lg:grid-cols-[1.3fr_1fr]">
              <div>
                <h2 className="text-3xl font-extrabold md:text-4xl" style={{ textWrap: "balance" }}>Punya kandidat lokasi? Kita lihat skornya.</h2>
                <p className="mt-4 max-w-xl leading-relaxed text-brand-100/80">
                  Daftar dulu, lalu tim kami menghubungi Anda untuk aktivasi. Atau langsung ngobrol — kami senang membahas rencana ekspansi Anda.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                <button onClick={goAuth} className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 font-semibold text-brand-800 transition-colors hover:bg-brand-50">
                  Daftar <ArrowRight className="h-4 w-4" />
                </button>
                <a href={WA_URL} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-7 py-3.5 font-semibold text-white transition-colors hover:bg-white/10">
                  <MessageCircle className="h-4 w-4" /> Chat WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-6 py-10 md:flex-row md:items-center md:justify-between md:px-10">
          <div>
            <p className="text-lg font-extrabold text-brand-700">Landhep</p>
            <p className="text-sm text-slate-500">See beyond the map.</p>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-slate-600">
            <a href={`mailto:${MAIL}`} className="inline-flex items-center gap-2 hover:text-brand-700"><Mail className="h-4 w-4" /> {MAIL}</a>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-brand-700"><MessageCircle className="h-4 w-4" /> +62 851-1121-4177</a>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <a href="/privasi" className="hover:text-brand-700">Kebijakan Privasi</a>
            <span>© {new Date().getFullYear()} Landhep</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
